import React from "react";

const BPRSForm = ({ formData, handleChange }) => {
    const items = [
        { id: "somaticConcern", title: "1. Somatic Concern", description: "Degree of concern over present bodily health. Rate the degree to which physical health is perceived as a problem by the patient, whether complaints have a realistic basis or not." },
        { id: "anxiety", title: "2. Anxiety", description: "Worry, fear, or over-concern for present or future. Rate solely on the basis of verbal report of patient’s own subjective experiences. Do not infer anxiety from physical signs or from neurotic defense mechanisms." },
        { id: "emotionalWithdrawal", title: "3. Emotional Withdrawal", description: "Deficiency in relating to the interviewer and to the interview situation. Rate only the degree to which the patient gives the impression of failing to be in emotional contact with other people in the interview situation." },
        { id: "conceptualDisorganization", title: "4. Conceptual Disorganization", description: "Degree to which the thought processes are confused, disconnected, or disorganized. Rate on the basis of integration of the verbal products of the patient." },
        { id: "guiltFeelings", title: "5. Guilt Feelings", description: "Over-concern or remorse for past behavior. Rate on the basis of the patient’s subjective experiences of guilt as evidenced by verbal report with appropriate affect." },
        { id: "tension", title: "6. Tension", description: "Physical and motor manifestations of tension, “nervousness”, and heightened activation level. Rate solely on the basis of physical signs and motor behavior." },
        { id: "mannerismsPosturing", title: "7. Mannerisms and Posturing", description: "Unusual and unnatural motor behavior, the type of motor behavior which causes certain mental patients to stand out in a crowd of normal people." },
        { id: "grandiosity", title: "8. Grandiosity", description: "Exaggerated self-opinion, conviction of unusual ability or powers. Rate only on the basis of patient’s statements about himself." },
        { id: "depressiveMood", title: "9. Depressive Mood", description: "Despondency in mood, sadness. Rate only degree of despondency; do not rate on the basis of inferences concerning depression based upon general retardation and somatic complaints." },
        { id: "hostility", title: "10. Hostility", description: "Animosity, contempt, belligerence, disdain for other people outside the interview situation. Rate solely on the basis of the verbal report of feelings and actions of the patient toward others." },
        { id: "suspiciousness", title: "11. Suspiciousness", description: "Belief (delusional or otherwise) that others have now, or have had in the past, malicious or discriminatory intent toward the patient." },
        { id: "hallucinatoryBehavior", title: "12. Hallucinatory Behavior", description: "Perceptions without normal external stimulus correspondence. Rate only those experiences which are reported to have occurred within the last week." },
        { id: "motorRetardation", title: "13. Motor Retardation", description: "Reduction in energy level evidenced in slowed movements. Rate on the basis of observed behavior of the patient only." },
        { id: "uncooperativeness", title: "14. Uncooperativeness", description: "Evidence of resistance, unfriendliness, resentment, and lack of readiness to cooperate with the interviewer." },
        { id: "unusualThoughtContent", title: "15. Unusual Thought Content", description: "Unusual, odd, strange or bizarre thought content. Rate the degree of unusualness, not the degree of disorganization of thought processes." },
        { id: "bluntedAffect", title: "16. Blunted Affect", description: "Reduced emotional tone, apparent lack of normal feeling or involvement." },
        { id: "excitement", title: "17. Excitement", description: "Heightened emotional tone, agitation, increased reactivity." },
        { id: "disorientation", title: "18. Disorientation", description: "Confusion or lack of proper association for person, place or time." }
    ];

    const scale = [
        { value: 0, label: "0 = Not assessed" },
        { value: 1, label: "1 = Not present" },
        { value: 2, label: "2 = Very mild" },
        { value: 3, label: "3 = Mild" },
        { value: 4, label: "4 = Moderate" },
        { value: 5, label: "5 = Moderately severe" },
        { value: 6, label: "6 = Severe" },
        { value: 7, label: "7 = Extremely severe" }
    ];

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + (parseInt(formData[item.id] || 0, 10)), 0);
    };

    const totalScore = calculateTotal();

    return (
        <>
            {/* Sticky Guidelines Button */}
            <div className="row mb-4 position-sticky top-0 bg-white pt-3" style={{ zIndex: 10 }}>
                <div className="col-md-3">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm w-100"
                        data-bs-toggle="modal"
                        data-bs-target="#bprsGuidelines"
                    >
                        <i className="ri-information-line me-2"></i>
                        BPRS Guidelines & Scoring
                    </button>
                </div>
            </div>
            <div className="p-3" style={{ maxHeight: "60vh", overflowY: "auto", scrollbarWidth: "thin" }}>

                <div className="accordion" id="bprsAccordion">
                    {/* Client Information Section */}
                    <div className="accordion-item mb-3 shadow-sm rounded">
                        <h2 className="accordion-header">
                            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#clientInfo">
                                Client Information
                            </button>
                        </h2>
                        <div id="clientInfo" className="accordion-collapse collapse show">
                            <div className="accordion-body pt-3">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="clientName"
                                                value={formData.clientName || ""}
                                                onChange={handleChange}
                                                readOnly={!!formData.patientId} // ⭐ ADD THIS PROPERTY ⭐
                                            />
                                            <label>Client Name</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="clientId" value={formData.clientId || ""} onChange={handleChange} />
                                            <label>Client ID#</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="md" value={formData.md || ""} onChange={handleChange} />
                                            <label>MD</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Each BPRS Item - Collapsible with info button */}
                    {items.map((item) => (
                        <div className="accordion-item mb-3 shadow-sm rounded" key={item.id}>
                            <h2 className="accordion-header d-flex align-items-center">
                                <button
                                    className={`accordion-button`}
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#item_${item.id}`}
                                >
                                    <span className="me-3 fw-bold">{item.title.split(".")[0]}.</span>
                                    <span>{item.title.split(". ")[1]}</span>
                                </button>
                                <div className="d-flex align-items-center mx-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm rounded-circle"
                                        style={{ width: 32, height: 32 }}
                                        data-bs-toggle="modal"
                                        data-bs-target={`#modal_${item.id}`}
                                    >
                                        <i className="ri-information-line fs-6"></i>
                                    </button>
                                </div>
                            </h2>
                            <div id={`item_${item.id}`} className="accordion-collapse collapse">
                                <div className="accordion-body pt-4">
                                    <div className="row g-4">
                                        {scale.map((opt) => (
                                            <div className="col-lg-3" key={opt.value}>
                                                <div className="form-check mb-3">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name={item.id}
                                                        id={`${item.id}_opt${opt.value}`}
                                                        value={opt.value}
                                                        data-label={opt.label}    // <── REQUIRED!
                                                        checked={formData[item.id] === String(opt.value)}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium d-flex align-items-start" htmlFor={`${item.id}_opt${opt.value}`}>
                                                        <span>{opt.label}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Individual Item Guideline Modal */}
                            <div className="modal fade" id={`modal_${item.id}`} tabIndex="-1">
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-body">
                                            <p className="small">{item.description}</p>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Main Guidelines Modal */}
                <div className="modal fade" id="bprsGuidelines" tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-body">
                                <p className="small mb-3"><strong>Scoring Scale:</strong></p>
                                <ul className="small mb-4">
                                    <li><strong>0</strong> = not assessed</li>
                                    <li><strong>1</strong> = not present</li>
                                    <li><strong>2</strong> = very mild</li>
                                    <li><strong>3</strong> = mild</li>
                                    <li><strong>4</strong> = moderate</li>
                                    <li><strong>5</strong> = moderately severe</li>
                                    <li><strong>6</strong> = severe</li>
                                    <li><strong>7</strong> = extremely severe</li>
                                </ul>
                                <p className="small"><strong>Total Score Range:</strong> 18–126<br />
                                    Higher scores indicate more severe psychiatric symptoms.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-primary" data-bs-dismiss="modal">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BPRSForm;